// frontend/src/components/admin/ReconciliationTab.tsx
// Cost-accounting Tier 3: estimated (usage_events) vs billed (Anthropic Admin
// Cost API) per UTC day × model, with a drift alarm. Sentry-tab pattern.
import {
  useCostReconciliation,
  type ReconciliationRow,
} from "@/hooks/useCostReconciliation";

function fmtUsd(v: number): string {
  return `$${v.toFixed(2)}`;
}

function fmtDrift(v: number | null): string {
  if (v === null) return "—";
  const pct = v * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

function driftColor(v: number | null, alarm: boolean): string {
  if (alarm) return "text-red-400";
  if (v === null) return "text-zinc-400";
  return Math.abs(v) > 0.1 ? "text-amber-400" : "text-emerald-400";
}

function Tile({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-4">
      <div className="text-xs uppercase tracking-wide text-zinc-400 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({ row }: { row: ReconciliationRow }) {
  return (
    <tr className={row.alarm ? "bg-red-950/40" : ""}>
      <td className="px-3 py-1.5 text-zinc-300 whitespace-nowrap">{row.day}</td>
      <td className="px-3 py-1.5 text-zinc-300 font-mono text-xs">{row.model}</td>
      <td className="px-3 py-1.5 text-right text-zinc-300">{fmtUsd(row.estimated_usd)}</td>
      <td className="px-3 py-1.5 text-right text-zinc-300">{fmtUsd(row.billed_usd)}</td>
      <td className="px-3 py-1.5 text-right text-zinc-400">{fmtUsd(row.delta_usd)}</td>
      <td className={`px-3 py-1.5 text-right ${driftColor(row.drift_pct, row.alarm)}`}>
        {fmtDrift(row.drift_pct)}
        {row.alarm && <span className="ml-1.5 text-xs font-semibold">⚠</span>}
      </td>
    </tr>
  );
}

export function ReconciliationTab() {
  const { data, isLoading, isError } = useCostReconciliation();

  if (isLoading) {
    return <div className="text-sm text-zinc-400 py-8">Loading reconciliation…</div>;
  }
  if (isError || !data) {
    return (
      <div className="rounded-lg border border-red-800 bg-zinc-900 p-6 text-sm text-red-300">
        Failed to load reconciliation data — check the backend logs.
      </div>
    );
  }
  if (!data.configured) {
    return (
      <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-6 text-sm text-zinc-300">
        <p className="font-medium mb-2">Cost reconciliation isn't configured.</p>
        <p className="text-zinc-400">
          Create an org <span className="font-mono">Admin API key</span> in the
          Anthropic Console (sk-ant-admin01-…) and set{" "}
          <span className="font-mono text-zinc-200">ANTHROPIC_ADMIN_KEY</span> on
          Railway. This compares our estimated Anthropic spend against actual
          billing, per day and model.
        </p>
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="rounded-lg border border-amber-800 bg-zinc-900 p-6 text-sm text-amber-300">
        Anthropic Admin API unreachable — showing nothing rather than stale
        numbers. Try again in a minute.
      </div>
    );
  }

  const s = data.summary;
  return (
    <div className="space-y-4">
      {s?.drift_alarm && (
        <div className="rounded-lg border border-red-800 bg-red-950/50 px-4 py-3 text-sm text-red-300">
          <span className="font-semibold">Drift alarm:</span> estimated vs billed
          diverges beyond 10% (and $0.50) on at least one day×model — worst is{" "}
          <span className="font-mono">{s.worst_model}</span> on {s.worst_day} (
          {fmtUsd(s.worst_delta_usd ?? 0)} off).
        </div>
      )}

      {(s?.excluded_billed_usd ?? 0) > 0 && (
        <p className="text-xs text-zinc-500">
          Harness/other-workspace spend excluded from drift:{" "}
          <span className="text-zinc-300">{fmtUsd(s!.excluded_billed_usd)}</span>
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label={`Estimated (${data.window_days ?? 14}d)`}>
          <div className="text-3xl font-semibold text-zinc-100">
            {fmtUsd(s?.estimated_usd ?? 0)}
          </div>
        </Tile>
        <Tile label={`Billed (${data.window_days ?? 14}d)`}>
          <div className="text-3xl font-semibold text-zinc-100">
            {fmtUsd(s?.billed_usd ?? 0)}
          </div>
        </Tile>
        <Tile label="Drift (billed vs estimated)">
          <div
            className={`text-3xl font-semibold ${driftColor(
              s?.drift_pct ?? null,
              s?.drift_alarm ?? false,
            )}`}
          >
            {fmtDrift(s?.drift_pct ?? null)}
          </div>
        </Tile>
        <Tile label="Worst day × model">
          {s?.worst_model ? (
            <div className="text-sm text-zinc-300">
              <div className="font-mono text-xs">{s.worst_model}</div>
              <div className="text-zinc-400">
                {s.worst_day} · {fmtUsd(s.worst_delta_usd ?? 0)} off
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-500">No rows in window</div>
          )}
        </Tile>
      </div>

      {(s?.unmatched_billed_models.length ?? 0) > 0 && (
        <div className="rounded-lg border border-amber-800 bg-zinc-900 px-4 py-3 text-sm text-amber-300">
          Billed by Anthropic but never estimated (missed usage capture?):{" "}
          <span className="font-mono">{s!.unmatched_billed_models.join(", ")}</span>
        </div>
      )}
      {(s?.unmatched_estimated_models.length ?? 0) > 0 && (
        <div className="rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm text-zinc-400">
          Estimated but absent from Anthropic billing:{" "}
          <span className="font-mono">{s!.unmatched_estimated_models.join(", ")}</span>
        </div>
      )}

      <div className="rounded-lg border border-zinc-700 bg-zinc-900 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-700">
              <th className="px-3 py-2">Day (UTC)</th>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2 text-right">Estimated</th>
              <th className="px-3 py-2 text-right">Billed</th>
              <th className="px-3 py-2 text-right">Δ</th>
              <th className="px-3 py-2 text-right">Drift</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {data.rows.map((row) => (
              <Row key={`${row.day}-${row.model}`} row={row} />
            ))}
          </tbody>
        </table>
        {data.rows.length === 0 && (
          <div className="px-4 py-6 text-sm text-zinc-500">
            No usage or billing in the window yet — data appears ~5 minutes
            after API traffic, complete days only.
          </div>
        )}
      </div>
    </div>
  );
}
